import { cn } from "@/lib/utils";
import { ReactNode } from "react";

export type IconToggleOption<T extends string> = {
  value: T;
  /** Accessible label describing the option (used for aria-label). */
  label: string;
  icon: ReactNode;
};

const ToggleButton = ({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    aria-pressed={active}
    onClick={onClick}
    className={cn(
      "flex h-[26px] w-[34px] items-center justify-center rounded-md transition-colors",
      active
        ? "bg-neutral-800 text-primary-active"
        : "text-neutral-500 hover:text-neutral-400",
    )}
  >
    {children}
  </button>
);

/**
 * Segmented toggle rendering an icon per option. Generic over the option value
 * so callers can drive it with their own union type.
 */
export const IconToggle = <T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: IconToggleOption<T>[];
  onChange: (value: T) => void;
}) => (
  <div className="flex gap-[3px] rounded-lg border border-neutral-800 bg-neutral-900 p-[3px]">
    {options.map((option) => (
      <ToggleButton
        key={option.value}
        active={value === option.value}
        onClick={() => onChange(option.value)}
        label={option.label}
      >
        {option.icon}
      </ToggleButton>
    ))}
  </div>
);
